import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Check, FileText, ChevronRight } from 'lucide-react';
import { useWorkframeList, useWorkframe, useCreateWorkframe } from '../../hooks/useWorkframes';
import { useDocsList } from '../../../docs/hooks/useDocsData';

// Menú "Cuadros de trabajo" del header del panel de tareas (integración main→dev, epic D1.3).
// Permite elegir "Ninguno", un cuadro existente, o crear una "Estructura de actualizaciones"
// buscando y vinculando un documento del espacio. Datos vía `useWorkframes` + `useDocsList`.

export default function TaskWorkframeDropdown({
    spaceId,
    currentWorkframeId,
    onSelectWorkframe,
}) {
    const { t } = useTranslation('tasks');
    const effectiveSpaceId = spaceId && spaceId !== 'all' ? spaceId : null;

    const [isOpen, setIsOpen] = useState(false);
    const [showDocSearch, setShowDocSearch] = useState(false);
    const [docSearchTerm, setDocSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const { data: savedWorkframes = [] } = useWorkframeList(effectiveSpaceId);
    const { data: resolvedWorkframe } = useWorkframe(currentWorkframeId);
    const createWorkframe = useCreateWorkframe(effectiveSpaceId);
    const { data: spaceDocs = [], isLoading: loadingDocs } = useDocsList(showDocSearch ? effectiveSpaceId : null);

    const activeWorkframe = useMemo(() => {
        if (!currentWorkframeId) return null;
        return savedWorkframes.find((w) => String(w.id) === String(currentWorkframeId)) || resolvedWorkframe || null;
    }, [currentWorkframeId, savedWorkframes, resolvedWorkframe]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setShowDocSearch(false);
                setDocSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const filteredDocs = useMemo(() => {
        const active = spaceDocs.filter((d) => (d.active_status || 'active') !== 'trash');
        const term = docSearchTerm.trim().toLowerCase();
        if (!term) return active.slice(0, 10);
        return active.filter((d) => (d.title || '').toLowerCase().includes(term)).slice(0, 10);
    }, [spaceDocs, docSearchTerm]);

    const closeMenu = () => {
        setIsOpen(false);
        setShowDocSearch(false);
        setDocSearchTerm('');
    };

    const handleSelectNone = () => {
        onSelectWorkframe(null);
        closeMenu();
    };

    const handleSelectSaved = (workframe) => {
        onSelectWorkframe(workframe.id);
        closeMenu();
    };

    const handleSelectDoc = async (doc) => {
        try {
            const res = await createWorkframe.mutateAsync({
                space_id: effectiveSpaceId,
                doc_id: doc.id,
                name: doc.title || t('workframe.dropdown.updatesStructure'),
                type: 'updates_structure',
            });
            if (res?.success && res.workframe) {
                onSelectWorkframe(res.workframe.id);
                closeMenu();
            }
        } catch {
            /* toast lo maneja el contenedor si lo necesita; el menú simplemente no cambia */
        }
    };

    const typeLabel = activeWorkframe?.type === 'updates_structure'
        ? t('workframe.dropdown.updatesStructure')
        : t('workframe.dropdown.workframe');

    return (
        <div className="wf-dropdown-wrapper" ref={dropdownRef}>
            <button
                type="button"
                className={`wf-trigger-tag ${currentWorkframeId ? 'active' : ''}`}
                onClick={() => setIsOpen((prev) => !prev)}
                title={activeWorkframe ? `${activeWorkframe.name} (${typeLabel})` : t('workframe.dropdown.tooltip')}
            >
                {currentWorkframeId ? (
                    <>
                        <span className="wf-trigger-type">{t('workframe.dropdown.structurePrefix')}</span>
                        <span className="wf-trigger-name">{activeWorkframe?.name || t('workframe.dropdown.loading')}</span>
                        <MoreHorizontal size={11} className="wf-trigger-dots" />
                    </>
                ) : (
                    <MoreHorizontal size={14} />
                )}
            </button>

            {isOpen && (
                <div className="wf-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="wf-dropdown-title">{t('workframe.dropdown.title')}</div>

                    <div
                        className={`wf-dropdown-item ${!currentWorkframeId ? 'active' : ''}`}
                        onClick={handleSelectNone}
                    >
                        <span>{t('workframe.dropdown.none')}</span>
                        {!currentWorkframeId && <Check size={13} />}
                    </div>

                    {savedWorkframes.length > 0 && (
                        <>
                            <div className="wf-dropdown-divider" />
                            {savedWorkframes.map((wf) => (
                                <div
                                    key={wf.id}
                                    className={`wf-dropdown-item ${String(currentWorkframeId) === String(wf.id) ? 'active' : ''}`}
                                    onClick={() => handleSelectSaved(wf)}
                                    title={wf.name}
                                >
                                    <span className="wf-dropdown-item-name">{wf.name}</span>
                                    {String(currentWorkframeId) === String(wf.id) && <Check size={13} />}
                                </div>
                            ))}
                        </>
                    )}

                    <div className="wf-dropdown-divider" />

                    {!showDocSearch ? (
                        <div className="wf-dropdown-item" onClick={() => setShowDocSearch(true)}>
                            <span>{t('workframe.dropdown.updatesStructure')}</span>
                            <ChevronRight size={13} color="#666" />
                        </div>
                    ) : (
                        <div className="wf-search-doc-input-container">
                            <span className="wf-search-doc-label">{t('workframe.dropdown.linkToDoc')}</span>
                            <input
                                type="text"
                                placeholder={t('workframe.dropdown.docSearchPlaceholder')}
                                value={docSearchTerm}
                                onChange={(e) => setDocSearchTerm(e.target.value)}
                                className="wf-search-doc-input"
                                autoFocus
                            />
                            <div className="wf-doc-search-results">
                                {loadingDocs ? (
                                    <div className="wf-doc-search-hint">{t('workframe.dropdown.loadingDocs')}</div>
                                ) : filteredDocs.length === 0 ? (
                                    <div className="wf-doc-search-hint">{t('workframe.dropdown.noDocs')}</div>
                                ) : (
                                    filteredDocs.map((doc) => (
                                        <div key={doc.id} className="wf-doc-search-item" onClick={() => handleSelectDoc(doc)}>
                                            <FileText size={12} color="#94a3b8" />
                                            <span className="wf-doc-search-item-name">
                                                {doc.title || t('workframe.index.untitled')}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
